import { Maybe, PostStage } from '@/graphql/generated/types.generated';

/**
 * Stages a post without a publish date can legitimately be in — every workflow stage except the ones
 * that require a date by definition (SCHEDULED, PUBLISHED, PUBLISHED_FAILED) and ARCHIVE.
 *
 * The client stages belong here for exactly the reason the team ones do: nothing requires a publish
 * date to reach them, so an idea can travel IDEA → TEAM_REVIEW → TEAM_APPROVED → CLIENT_REVIEW and
 * still have no date. While they were commented out, the ideas list dropped such a post the moment
 * it was sent to the client.
 *
 * Note this is deliberately BROADER than the server's `ideas` query, which returns only
 * `stage = IDEA`. It governs where a post lands in local state after a create or update, so it has
 * to describe "could exist without a date", not "is returned by that query".
 */
export const IDEA_STAGES: PostStage[] = [
    PostStage.Idea,
    PostStage.Draft,
    PostStage.TeamReview,
    PostStage.TeamRevision,
    PostStage.TeamApproved,
    PostStage.ClientReview,
    PostStage.ClientRevision,
    PostStage.ClientApproved,
];

/**
 * A post is an idea because it has no date, not because of its stage. It keeps whatever stage the
 * workflow gave it (e.g. TEAM_REVIEW) until a date places it on the calendar.
 */
export const postIsIdea = (post: { publishAt?: Maybe<string>; stage: PostStage }): boolean => {
    return !post.publishAt && IDEA_STAGES.includes(post.stage);
};
